import React, { useEffect, useState } from "react";
import { Bank, CheckCircle, Clock, CurrencyDollarSimple, ArrowClockwise, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUS_BADGE = {
  manual_review: { color: "#EAB308", label: "Manual review" },
  paid: { color: "#00E5FF", label: "Paid" },
  pending: { color: "#FF3B6B", label: "Pending" },
};

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("manual_review");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get(`/admin/payouts?status=${statusFilter}`),
        api.get("/admin/payouts/stats"),
      ]);
      setPayouts(p.data || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payouts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const markPaid = async (payout_id, amount) => {
    if (!window.confirm(`Confirm you've sent ₹${amount.toFixed(2)} to the ambassador. This cannot be undone.`)) return;
    setBusyId(payout_id);
    try {
      await api.post(`/admin/payouts/${payout_id}/mark-paid`);
      toast.success(`Marked ₹${amount.toFixed(2)} as paid`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="admin-payouts-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// ADMIN · PAYOUTS</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Affiliate payouts queue</h1>
          <p className="text-white/50 mt-1">Manually confirm payouts you've sent via UPI/bank/RazorpayX.</p>
        </div>
        <button onClick={load} className="glass rounded-lg px-3 py-2 text-sm flex items-center gap-2 hover:bg-white/5" data-testid="admin-payouts-refresh">
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>

      {/* Stats cards */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { key: "manual_review", label: "Awaiting review", icon: Clock, color: "#EAB308" },
          { key: "paid", label: "Paid lifetime", icon: CheckCircle, color: "#00E5FF" },
          { key: "pending", label: "Pending automation", icon: CurrencyDollarSimple, color: "#FF3B6B" },
        ].map(s => (
          <div key={s.key} className="glass rounded-xl p-5" data-testid={`admin-stat-${s.key}`}>
            <s.icon size={20} weight="duotone" style={{ color: s.color }} />
            <p className="mt-3 text-xs uppercase tracking-wider text-white/50">{s.label}</p>
            <p className="mt-1 font-mono text-2xl">
              ₹{(stats?.[s.key]?.amount ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-white/40 mt-0.5">{stats?.[s.key]?.count ?? 0} payouts</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="mt-8 inline-flex glass rounded-lg p-1" data-testid="admin-status-tabs">
        {["manual_review", "paid", "pending"].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded text-sm transition-colors ${
              statusFilter === s ? "bg-[#FF3B6B] text-white" : "text-white/60 hover:text-white"
            }`}
            data-testid={`admin-tab-${s}`}
          >
            {STATUS_BADGE[s].label}
          </button>
        ))}
      </div>

      {/* Payouts list */}
      <div className="mt-6 glass rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/40">Loading payouts…</div>
        ) : payouts.length === 0 ? (
          <div className="p-10 text-center" data-testid="admin-payouts-empty">
            <Bank size={48} weight="duotone" className="mx-auto mb-3 text-white/20" />
            <p className="text-white/60">No payouts in <span className="text-[#FF3B6B]">{STATUS_BADGE[statusFilter].label.toLowerCase()}</span> status.</p>
          </div>
        ) : (
          payouts.map(p => (
            <div key={p.id} className="flex items-center gap-4 p-4 border-b border-white/5 last:border-none" data-testid={`admin-payout-${p.id}`}>
              <Crown size={22} weight="duotone" className="text-[#EAB308] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.ambassador?.name || "Unknown"}</p>
                <p className="text-xs text-white/50 truncate">{p.ambassador?.email || "—"} · {new Date(p.created_at).toLocaleDateString()}</p>
                {p.note && <p className="text-xs text-white/40 mt-1 italic">{p.note}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-lg text-[#FF3B6B] font-bold">₹{p.amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-white/40">{p.event_ids?.length || 0} event{p.event_ids?.length === 1 ? "" : "s"}</p>
              </div>
              {statusFilter === "manual_review" ? (
                <button
                  onClick={() => markPaid(p.id, p.amount)}
                  disabled={busyId === p.id}
                  className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 shrink-0"
                  data-testid={`admin-mark-paid-${p.id}`}
                >
                  {busyId === p.id ? "Marking…" : "Mark paid"}
                </button>
              ) : (
                <span
                  className="text-xs px-2 py-1 rounded-full font-mono shrink-0"
                  style={{ background: `${STATUS_BADGE[p.status]?.color}20`, color: STATUS_BADGE[p.status]?.color }}
                >
                  {STATUS_BADGE[p.status]?.label || p.status}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
